<template>
  <LoaderFullPage v-if="loading" />
  <form
    @submit.prevent="formSubmit"
    class="onboarding-form"
    :class="{ 'is-tablet': isSmallerThanLaptop }"
    v-else
  >
    <header class="form-header">
      <EnteteOnboardingMinerve
        etape="Étape 1 sur 2"
        titre="Votre première veille"
        texte="Choisissez vos thématiques et vos mots-clés. Minerve commence à lire dès que vous validez."
        expression="clin"
        :mouvements="['wave']"
      />
      <p class="form-header__text title-large-semibold">
        Veuillez choisir 3 sous-thématiques
      </p>
      <p class="form-header__text title-medium-regular">
        Le contenu de la plateforme sera adapté à vos intérêts.
      </p>
      <p class="form-header__text title-small-regular">
        Pas d'inquiétude! Vous pourrez les modifier et en choisir davantage plus
        tard.
      </p>
    </header>
    <section ref="selectionSection" class="selection-section sticky-section">
      <SelectionCard :selected-sub-thematic-list="selectedSubThematicList" />
    </section>
    <section v-if="defaultThematic" class="thematics-wrapper">
      <p class="title-medium-semibold">Nos suggestions :</p>
      <div class="suggested-sector">
        <p class="title-medium-regular">{{ defaultThematic?.name }}</p>
      </div>

      <MultipleCheck
        :sub-thematic-list="defaultThematic.subthematics"
        v-model="selection[defaultThematic._id]"
        :disabled="isValid"
      />
    </section>
    <section class="search-section sticky-section">
      <p class="title-medium-semibold">
        Trouvez des sous-thématiques sur base de vos mots-clés :
      </p>
      <SearchBar
        placeholder="Cherche un mot clé"
        v-model:search-query="searchQueryInput"
      />
    </section>

    <section class="thematics-wrapper">
      <CollapseSection
        v-for="thematic in otherThematicList"
        :key="thematic._id"
        start-closed
      >
        <template #title>
          <p class="title-medium-regular">{{ thematic.name }}</p>
        </template>
        <template #content>
          <MultipleCheck
            :sub-thematic-list="thematic.subthematics"
            :search-query="
              matchThematic(thematic, searchQueryInput) ? '' : searchQueryInput
            "
            v-model="selection[thematic._id]"
            :disabled="isValid"
          />
        </template>
      </CollapseSection>
    </section>

    <footer class="btn-wrapper" :class="{ 'is-tablet': isSmallerThanLaptop }">
      <Button
        v-focus
        type="submit"
        size="medium"
        color="primary"
        mode="light"
        :class="[
          `btn`,
          { 'btn--primary': selectedSubThematicList.length === 3 },
        ]"
        :disabled="!isValid || envoiEnCours"
      >
        <template v-if="envoiEnCours">
          <MinerveOrbite :taille="16" corps="#fff" :libelle="null" />
          Enregistrement en cours
        </template>
        <template v-else
          >Continue {{ selectedSubThematicList.length }}/3</template
        >
      </Button>
    </footer>
  </form>
</template>
<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import LoaderFullPage from "@follaw-fr/components/reusable/molecules/LoaderFullPage.vue";
import Button from "@follaw-fr/components/reusable/atoms/Button.vue";
import EnteteOnboardingMinerve from "@follaw-fr/components/onboarding/molecules/EnteteOnboardingMinerve.vue";
import MinerveOrbite from "@follaw-fr/components/reusable/minerve/MinerveOrbite.vue";
import SelectionCard from "../../components/onboarding/molecules/SelectionCard.vue";
import MultipleCheck from "../../components/onboarding/molecules/MultipleCheck.vue";
import CollapseSection from "@follaw-fr/components/reusable/atoms/CollapseSection.vue";
import SearchBar from "@follaw-fr/components/reusable/atoms/SearchBar.vue";
import {
  createOnboardingDashboardAndDigest,
  getOnboardingThematics,
} from "@follaw-fr/services/apiOnboarding";
import { useUserSettingsStore } from "@follaw-fr/stores/user-settings.js";
import type {
  SubThematicReference,
  ThematicReference,
} from "@commons/types/api/schema.types";
import type { CreateOnboardingDashboardAndDigestPayload } from "@commons/types/api/api.types";
import { searchNormalized } from "@follaw-fr/utils/helper.utils";
import { unSeulEnvoi } from "@follaw-fr/utils/un-seul-envoi";
import { useResizeObserver } from "@vueuse/core";
import { useUserStore } from "@/follaw-fr/stores/user";

import { useRouter } from "vue-router";

import { useResponsive } from "@/commons/composables/useResponsive";

const store = useUserSettingsStore();
const userStore = useUserStore();

const router = useRouter();
const { isSmallerThanLaptop } = useResponsive();

const loading = ref<boolean>(false);
const thematics = ref<ThematicReference[]>([]);
const sector = ref<number | null>();
const selection = ref<Record<string, SubThematicReference[]>>({});
const searchQueryInput = ref<string>("");

const defaultThematic = computed(() =>
  thematics.value.find((item) => item._id === sector.value)
);
const otherThematicList = computed(() =>
  thematics.value.filter((item) => {
    return (
      item._id !== sector.value &&
      item.subthematics.some((subThematic) =>
        searchNormalized(subThematic.name, searchQueryInput.value)
      )
    );
  })
);

const selectedSubThematicList = computed(() =>
  Object.values(selection.value).flat()
);

const isValid = computed(() => selectedSubThematicList.value.length === 3);

const matchThematic = (thematic: ThematicReference, searchQuery: string) =>
  searchNormalized(thematic.name, searchQuery);

const loadUserThematics = async () => {
  loading.value = true;
  try {
    if (store.user) {
      const data = await getOnboardingThematics();
      if (data) {
        sector.value = data.sector;
        thematics.value = data.thematics;
      }
    }
  } catch (err) {
    throw err;
  } finally {
    loading.value = false;
  }
};

loadUserThematics();

const submitSubthematicsSelection = async () => {
  if (selectedSubThematicList.value.length !== 3) {
    return;
  }

  const payload: CreateOnboardingDashboardAndDigestPayload = {
    subthematic_ids: selectedSubThematicList.value.map(
      (subthematic) => subthematic._id
    ) as [number, number, number],
  };

  try {
    await createOnboardingDashboardAndDigest(payload);
  } catch (err) {
    throw err;
  }
};

// Un seul envoi à la fois : chaque clic pendant l'envoi créait une veille et son récapitulatif de plus.
const { enCours: envoiEnCours, lancer: formSubmit } = unSeulEnvoi(async () => {
  await submitSubthematicsSelection();
  // Todo call this function at the end of the onboarding to retrieve user and filters data and start the product tour.
  // await userStore.getUserAndFilters();

  await userStore.getUserAndFilters();
  await router.push({ name: "Onboarding - first alerts" });
});

// Use a ref to retrieve the height of the selection section dynamically
const selectionSection = useTemplateRef("selectionSection");
const selectionSectionStickyOffset = ref<string>("0px");
useResizeObserver(selectionSection, (entries) => {
  const entry = entries[0];
  const offset = entry.borderBoxSize?.[0]
    ? entry.borderBoxSize?.[0].blockSize - 1
    : 0;
  selectionSectionStickyOffset.value = `${offset}px`;
});
</script>
<style scoped lang="scss">
.onboarding-form {
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  gap: 32px;
  height: 100%;

  &:not(.is-tablet) {
    padding-inline: 72px;
    position: absolute;
    left: 0;
    top: 0;
  }

  .sticky-section {
    background-color: white;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .selection-section {
    padding-top: 8px;
    padding-bottom: 8px;
    margin-bottom: 4px;
  }
  .search-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    top: v-bind(selectionSectionStickyOffset);
  }

  .thematics-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .suggested-sector {
    width: 100%;
    border-bottom: 1px solid var(--clr-p2);
  }
  .form-header {
    padding-top: 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    &__text {
      text-align: center;
      word-wrap: break-word;
      margin: 0;
    }
  }

  .btn-wrapper {
    padding-bottom: 32px;
    position: sticky;
    bottom: 0;
    z-index: 1;
    width: 100%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 50.62%);
    display: flex;
    justify-content: flex-end;

    &.is-tablet {
      padding-right: 32px;
      position: absolute;
      right: 0;
    }
  }
}
</style>
